<template>
  <component
    :is="displayState.layoutMode === 'mobile' ? 'el-drawer' : 'el-dialog'"
    v-model="dialogVisible"
    class="auth-login-dialog"
    modal-class="auth-login-overlay"
    :title="$t('views.login.login_title')"
    :width="displayState.layoutMode === 'mobile' ? undefined : '820px'"
    :size="displayState.layoutMode === 'mobile' ? '100%' : undefined"
    :direction="displayState.layoutMode === 'mobile' ? 'btt' : undefined"
    :style="loginViewportStyle"
    :show-close="false"
    :with-header="displayState.layoutMode === 'mobile' ? false : undefined"
    :close-on-click-modal="!submitting && !googleLoading"
    :close-on-press-escape="!submitting && !googleLoading"
    :destroy-on-close="false"
    append-to-body
    align-center
    @opened="handleDialogOpened"
    @closed="handleDialogClosed"
  >
    <div
      class="login-dialog-shell"
      :class="{ 'login-dialog-shell--third-party': shouldShowThirdPartyLogin }"
    >
      <button
        class="dialog-close"
        type="button"
        :disabled="submitting || googleLoading"
        :aria-label="$t('views.login.dialog_close')"
        @click="close"
      >
        <el-icon><Close /></el-icon>
      </button>

      <aside class="login-visual" aria-hidden="true">
        <div
          id="login-visual-blur-gradient-bg"
          ref="blurGradientHost"
          class="blur-gradient-background"
        ></div>

        <!-- 浮动气泡动效 -->
        <div class="floating-bubbles">
          <div class="bubble bubble--1"></div>
          <div class="bubble bubble--2"></div>
          <div class="bubble bubble--3"></div>
        </div>

        <!-- Logo 品牌标识 -->
        <div class="visual-brand-mark">
          <img :src="logoUrl" alt="">
        </div>

        <!-- 文案内容居中 -->
        <div class="visual-copy">
          <div class="visual-hero-title-wrapper">
            <h2 class="visual-hero-title">
              <span class="visual-hero-prefix">{{ $t('views.login.hero_title_prefix') }}</span>
              <span class="visual-hero-highlight">{{ $t('views.login.hero_title_highlight') }}</span>
            </h2>
          </div>

          <!-- 滚动切换文案 -->
          <div class="typewriter-wrapper">
            <transition name="fade-up" mode="out-in">
              <p :key="phraseIndex" class="visual-hero-phrase">{{ phrases[phraseIndex] }}</p>
            </transition>
          </div>
        </div>

        <!-- 三条特性 -->
        <ul class="visual-features">
          <li
            v-for="(feature, idx) in features"
            :key="idx"
            class="visual-feature-item"
            :class="`visual-feature-item--${idx + 1}`"
          >
            <span class="visual-feature-check">✓</span>
            <span>{{ feature }}</span>
          </li>
        </ul>
      </aside>

      <main class="login-content">
        <div class="mobile-brand">
          <img :src="logoUrl" alt="PAITEAMS">
          <span>PAITEAMS</span>
        </div>

        <header class="login-dialog-header">
          <h1 class="login-dialog-title">
            <span>{{ $t('views.login.login_title_prefix') }}</span>
            <span class="login-dialog-brand">PAITEAMS</span>
          </h1>
          <p>{{ $t('views.login.welcome_text') }}</p>
        </header>

        <div v-if="inviteInfo || inviteInvalid" class="invite-notice">
          <el-icon class="invite-notice-icon"><Promotion /></el-icon>
          <div>
            <p v-if="inviteInfo && inviteInfo.invite_type === 1" class="invite-title">
              <strong>{{ inviteInfo.inviter_user_name }}</strong>
              {{ $t('views.login.invite_you') }}
            </p>
            <p v-else-if="inviteInfo" class="invite-title">
              {{ $t('views.login.welcome_use') }}
            </p>
            <p v-if="inviteInfo" class="invite-reward">
              {{ $t('views.login.register_to_get') }}
              <strong>{{ formatNumber(inviteInfo.reward_points) }}</strong>
              {{ $t('views.login.new_user_points') }}
            </p>
            <p v-else class="invite-invalid">{{ $t('views.login.invite_invalid') }}</p>
          </div>
        </div>

        <div
          v-if="availableTabs.length > 1"
          class="login-tabs"
          :class="{ 'login-tabs--three': availableTabs.length === 3 }"
          role="tablist"
          :aria-label="$t('views.login.login_methods')"
        >
          <button
            v-for="tab in availableTabs"
            :key="tab.name"
            class="login-tab"
            :class="{ 'is-active': activeTab === tab.name }"
            type="button"
            role="tab"
            :aria-selected="activeTab === tab.name"
            @click="switchTab(tab.name)"
          >
            <el-icon aria-hidden="true">
              <Iphone v-if="tab.name === 'phone'" />
              <Message v-else-if="tab.name === 'email'" />
              <Lock v-else />
            </el-icon>
            <span>{{ $t(tab.label) }}</span>
          </button>
        </div>

        <form class="login-form" @submit.prevent="submitLogin">
          <div class="field-block">
            <div class="field-label-row">
              <label :for="contactInputId">{{ $t(contactLabelKey) }}</label>
              <span v-if="activeTab === 'phone'" class="country-code">+86</span>
            </div>
            <el-input
              :key="contactInputId"
              :id="contactInputId"
              v-model="contactValue"
              :type="activeTab === 'phone' ? 'tel' : 'text'"
              :autocomplete="activeTab === 'password' ? 'username' : (activeTab === 'phone' ? 'tel' : 'email')"
              :placeholder="$t(contactPlaceholderKey)"
              size="large"
              class="login-input"
              @input="handleContactInput"
            />
          </div>

          <div v-if="activeTab !== 'password'" class="field-block">
            <div class="field-label-row">
              <label :for="codeInputId">{{ $t('views.login.verification_code_label') }}</label>
            </div>
            <div class="code-input-row">
              <el-input
                :id="codeInputId"
                v-model="codeValue"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                :placeholder="$t(codePlaceholderKey)"
                size="large"
                class="login-input code-input"
                @input="handleCodeInput"
              />
              <button
                class="send-code-button"
                type="button"
                :disabled="codeSending || currentCountdown > 0"
                :aria-busy="codeSending"
                @click="sendCode"
              >
                <span v-if="currentCountdown > 0">
                  {{ $t('views.login.resend_after', { time: currentCountdown }) }}
                </span>
                <span v-else-if="codeSending">{{ $t('views.login.sending_code') }}</span>
                <span v-else>{{ $t('views.login.send_code') }}</span>
              </button>
            </div>
          </div>

          <div v-else class="field-block">
            <div class="field-label-row">
              <label for="login-password-value-input">{{ $t('views.login.password_title') }}</label>
            </div>
            <el-input
              id="login-password-value-input"
              v-model="passwordForm.password"
              type="password"
              autocomplete="current-password"
              show-password
              :placeholder="$t('views.login.please_enter_your_pwd')"
              size="large"
              class="login-input"
              @input="handlePasswordInput"
            />
          </div>

          <p v-if="inlineError" class="inline-error" role="alert">{{ inlineError }}</p>

          <el-checkbox v-model="agreed" class="agreement-check">
            <span>{{ $t('views.login.agreement_prefix') }}</span>
            <a :href="termsUrl" target="_blank" rel="noopener noreferrer" @click.stop>
              {{ $t('views.login.terms_of_service') }}
            </a>
            <span class="agreement-separator">、</span>
            <a :href="privacyUrl" target="_blank" rel="noopener noreferrer" @click.stop>
              {{ $t('views.login.privacy_policy') }}
            </a>
          </el-checkbox>

          <el-button
            class="submit-login-button"
            type="primary"
            native-type="submit"
            size="large"
            :loading="submitting"
            :disabled="!agreed || submitting || googleLoading || inviteLoading"
          >
            {{ submitting ? $t('views.login.login_in_progress') : $t('views.login.login_button') }}
          </el-button>
        </form>

        <template v-if="shouldShowThirdPartyLogin">
          <div class="login-divider" aria-hidden="true">
            <span></span>
            <em>{{ $t('views.login.other_login_methods') }}</em>
            <span></span>
          </div>

          <button
            v-if="isGoogleLoginEnabled"
            class="google-login-button"
            type="button"
            :disabled="googleLoading || submitting"
            @click="handleGoogleLogin"
          >
            <svg-icon icon-class="google" class-name="google-icon" />
            <span>{{ googleLoading ? $t('views.login.google_redirecting') : $t('views.login.google_login') }}</span>
          </button>

          <el-tooltip
            v-if="isWechatLoginEnabled"
            :content="$t('views.login.wechat_login_pending')"
            placement="top"
          >
            <button
              class="wechat-login-button"
              type="button"
              disabled
            >
              <svg-icon icon-class="wechat" class-name="wechat-icon" />
              <span>{{ $t('views.login.wechat_login') }}</span>
            </button>
          </el-tooltip>
        </template>

        <p class="mobile-login-registration">{{ $t('views.login.dialog_subtitle') }}</p>
      </main>
    </div>
  </component>
</template>

<script>
import { Close, Iphone, Lock, Message, Promotion } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import SvgIcon from '@/components/SvgIcon/index.vue'
import { getGoogleAuthUrl, getSysEmailCode, getSysSMSCode } from '@/api/user'
import { getInviteInfoByCode } from '@/api/invite'
import { def_logo_url } from '@/settings'
import { useDisplayState } from '@/views/mobile/shared/useDisplayState'

const PHONE_PATTERN = /^1[3-9]\d{9}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const CODE_PATTERN = /^\d{6}$/
const PHONE_LOGIN_ENABLED = process.env.VUE_APP_ENABLE_PHONE_LOGIN === 'true'
const PASSWORD_LOGIN_ENABLED = process.env.VUE_APP_ENABLE_PASSWORD_LOGIN === 'true'
const DEFAULT_LOGIN_TAB = PHONE_LOGIN_ENABLED ? 'phone' : 'email'
const REASON_I18N_KEYS = {
  auth_required: 'views.login.reason_auth_required',
  login: 'views.login.reason_auth_required',
  chat: 'views.login.reason_chat',
  knowledge: 'views.login.reason_knowledge',
  account: 'views.login.reason_account',
  subscription: 'views.login.reason_subscription',
  invite: 'views.login.reason_invite',
  image_recents: 'views.login.reason_image_recents',
  upload: 'views.login.reason_upload',
  image_generation: 'views.login.reason_image_generation',
  web_generation: 'views.login.reason_web_generation',
  payment_result: 'views.login.reason_payment_result'
}
const BLUR_GRADIENT_BACKGROUND_ID = 'login-visual-blur-gradient-bg'
const BLUR_GRADIENT_BASE_URL = process.env.BASE_URL || '/'
const BLUR_GRADIENT_SCRIPT_URL = `${BLUR_GRADIENT_BASE_URL.replace(/\/?$/, '/')}static/js/BlurGradientBg.min.js`
const BLUR_GRADIENT_COLORS = ['#8278f5', '#fe93cb', '#fcdcd1', '#ffcbc2']
let blurGradientScriptPromise = null

function loadBlurGradientScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('Browser environment is required'))

  if (window.Color4Bg && typeof window.Color4Bg.BlurGradientBg === 'function') {
    return Promise.resolve(window.Color4Bg.BlurGradientBg)
  }

  if (blurGradientScriptPromise) return blurGradientScriptPromise

  blurGradientScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = BLUR_GRADIENT_SCRIPT_URL
    script.async = true
    script.dataset.loginBlurGradient = 'true'
    script.onload = () => {
      const BlurGradientBg = window.Color4Bg && window.Color4Bg.BlurGradientBg
      if (typeof BlurGradientBg === 'function') {
        resolve(BlurGradientBg)
      } else {
        blurGradientScriptPromise = null
        reject(new Error('BlurGradientBg export is unavailable'))
      }
    }
    script.onerror = () => {
      blurGradientScriptPromise = null
      reject(new Error('BlurGradientBg script failed to load'))
    }
    document.head.appendChild(script)
  })

  return blurGradientScriptPromise
}

export default {
  setup() { return useDisplayState() },
  name: 'LoginDialog',
  components: { Close, Iphone, Lock, Message, Promotion, SvgIcon },
  emits: ['close', 'closed', 'login-success', 'success'],
  data() {
    return {
      logoUrl: def_logo_url,
      activeTab: DEFAULT_LOGIN_TAB,
      phoneLoginEnabled: PHONE_LOGIN_ENABLED,
      passwordLoginEnabled: PASSWORD_LOGIN_ENABLED,
      phoneForm: { phone: '', code: '' },
      emailForm: { email: '', code: '' },
      passwordForm: { username: '', password: '' },
      agreed: false,
      submitting: false,
      googleLoading: false,
      inlineError: '',
      inviteInfo: null,
      inviteInvalid: false,
      inviteLoading: false,
      inviteLookupCode: '',
      codeSending: false,
      countdowns: { phone: 0, email: 0 },
      timers: { phone: null, email: null },
      tabs: [
        { name: 'phone', label: 'views.login.phone_login' },
        { name: 'email', label: 'views.login.email_login' },
        { name: 'password', label: 'views.login.password_login' }
      ],
      termsUrl: '/legal/terms',
      privacyUrl: '/legal/privacy',
      isGoogleLoginEnabled: process.env.VUE_APP_ENABLE_GOOGLE_LOGIN === 'true',
      isWechatLoginEnabled: process.env.VUE_APP_ENABLE_GOOGLE_LOGIN !== 'true',
      showThirdPartyLogin: process.env.VUE_APP_ENABLE_GOOGLE_LOGIN === 'true',
      phraseIndex: 0,
      phraseInterval: null,
      blurGradientInstance: null,
      blurGradientOriginalUpdate: null,
      blurGradientPaused: false,
      blurGradientResizeHandler: null,
      blurGradientVendorResizeListener: null,
      blurGradientInitializing: false,
      blurGradientDisposed: false
    }
  },
  computed: {
    loginViewportStyle() {
      if (!this.displayState.isIPad) return null
      const viewport = this.displayState.viewport
      return {
        '--login-visible-height': `${viewport.height}px`,
        '--login-visible-width': `${viewport.width}px`,
        '--login-visible-top': `${viewport.top}px`,
        '--login-visible-left': `${viewport.left}px`
      }
    },
    dialogVisible: {
      get() {
        return this.$store.getters['authDialog/visible']
      },
      set(value) {
        if (!value) this.close()
      }
    },
    storeReason() {
      return this.$store.getters['authDialog/reason'] || ''
    },
    storeInviteCode() {
      return this.$store.getters['authDialog/inviteCode'] || ''
    },
    initialTab() {
      return this.$store.getters['authDialog/initialTab'] || DEFAULT_LOGIN_TAB
    },
    availableTabs() {
      return this.tabs.filter(tab => this.isTabEnabled(tab.name))
    },
    redirect() {
      return this.$store.getters['authDialog/redirect'] || ''
    },
    reasonText() {
      if (!this.storeReason) return ''
      if (REASON_I18N_KEYS[this.storeReason]) return this.$t(REASON_I18N_KEYS[this.storeReason])
      if (this.storeReason.startsWith('views.login.') || this.storeReason.startsWith('common.')) {
        return this.$t(this.storeReason)
      }
      return ''
    },
    contactValue: {
      get() {
        if (this.activeTab === 'phone') return this.phoneForm.phone
        if (this.activeTab === 'email') return this.emailForm.email
        return this.passwordForm.username
      },
      set(value) {
        if (this.activeTab === 'phone') {
          this.phoneForm.phone = value
        } else if (this.activeTab === 'email') {
          this.emailForm.email = value
        } else {
          this.passwordForm.username = value
        }
      }
    },
    codeValue: {
      get() {
        return this.activeTab === 'phone' ? this.phoneForm.code : this.emailForm.code
      },
      set(value) {
        if (this.activeTab === 'phone') {
          this.phoneForm.code = value
        } else {
          this.emailForm.code = value
        }
      }
    },
    contactInputId() {
      return `login-${this.activeTab}-input`
    },
    codeInputId() {
      return `login-${this.activeTab}-code-input`
    },
    contactLabelKey() {
      if (this.activeTab === 'phone') return 'views.login.phone_number'
      if (this.activeTab === 'password') return 'views.login.username_label'
      return 'views.login.email_address'
    },
    contactPlaceholderKey() {
      if (this.activeTab === 'phone') return 'views.login.phone_placeholder'
      if (this.activeTab === 'password') return 'views.login.username_placeholder'
      return 'views.login.email_placeholder'
    },
    codePlaceholderKey() {
      return this.activeTab === 'phone'
        ? 'views.login.phone_code_placeholder'
        : 'views.login.email_code_placeholder'
    },
    currentCountdown() {
      return this.countdowns[this.activeTab] || 0
    },
    effectiveInviteCode() {
      return this.storeInviteCode && !this.inviteInvalid ? this.storeInviteCode : ''
    },
    shouldShowThirdPartyLogin() {
      return this.showThirdPartyLogin && (this.isGoogleLoginEnabled || this.isWechatLoginEnabled)
    },
    phrases() {
      return [
        this.$t('views.login.hero_phrase_ready'),
        this.$t('views.login.hero_phrase_workflows'),
        this.$t('views.login.hero_phrase_deliver')
      ]
    },
    features() {
      return [
        this.$t('views.login.feature_automation'),
        this.$t('views.login.feature_security'),
        this.$t('views.login.feature_collaboration')
      ]
    }
  },
  watch: {
    dialogVisible(value) {
      if (value) {
        this.prepareForOpen()
        this.startTypewriter()
        this.$nextTick(() => this.initBlurGradientBackground())
      } else {
        this.stopTypewriter()
        this.pauseBlurGradientBackground()
      }
    },
    storeInviteCode(value) {
      if (this.dialogVisible && value) this.lookupInvite(value)
    },
    activeTab() {
      this.inlineError = ''
      this.$nextTick(() => {
        const input = document.getElementById(this.contactInputId)
        if (input) input.focus()
      })
    }
  },
  mounted() {
    this.blurGradientDisposed = false
    this.blurGradientResizeHandler = () => {
      if (!this.dialogVisible) return
      const host = this.$refs.blurGradientHost
      const hasVisibleHost = !!host && host.clientWidth > 0 && host.clientHeight > 0
      if (this.blurGradientInstance) {
        if (hasVisibleHost) {
          this.resumeBlurGradientBackground()
        } else {
          this.pauseBlurGradientBackground()
        }
        return
      }
      this.initBlurGradientBackground()
    }
    window.addEventListener('resize', this.blurGradientResizeHandler)

    if (this.dialogVisible) {
      this.prepareForOpen()
      this.startTypewriter()
      this.$nextTick(() => this.initBlurGradientBackground())
    }
  },
  beforeUnmount() {
    this.clearAllTimers()
    this.stopTypewriter()
    if (this.blurGradientResizeHandler) {
      window.removeEventListener('resize', this.blurGradientResizeHandler)
      this.blurGradientResizeHandler = null
    }
    this.destroyBlurGradientBackground()
    this.blurGradientDisposed = true
  },
  methods: {
    startTypewriter() {
      this.stopTypewriter()
      this.phraseInterval = setInterval(() => {
        this.phraseIndex = (this.phraseIndex + 1) % this.phrases.length
      }, 4000)
    },
    stopTypewriter() {
      if (this.phraseInterval) {
        clearInterval(this.phraseInterval)
        this.phraseInterval = null
      }
    },
    handleDialogOpened() {
      this.initBlurGradientBackground()
      this.resumeBlurGradientBackground()
    },
    async initBlurGradientBackground() {
      if (this.blurGradientDisposed || !this.dialogVisible || this.blurGradientInstance || this.blurGradientInitializing) return

      const host = this.$refs.blurGradientHost
      if (!host || host.clientWidth === 0 || host.clientHeight === 0) return

      this.blurGradientInitializing = true
      try {
        const BlurGradientBg = await loadBlurGradientScript()
        if (this.blurGradientDisposed || !this.dialogVisible || this.blurGradientInstance) return

        const currentHost = this.$refs.blurGradientHost
        if (!currentHost || currentHost.clientWidth === 0 || currentHost.clientHeight === 0) return

        let vendorResizeListener = null
        const nativeAddEventListener = window.addEventListener
        window.addEventListener = function (type, listener, options) {
          if (type === 'resize' && !vendorResizeListener) vendorResizeListener = listener
          return nativeAddEventListener.call(this, type, listener, options)
        }

        let instance
        try {
          instance = new BlurGradientBg({
            dom: BLUR_GRADIENT_BACKGROUND_ID,
            colors: BLUR_GRADIENT_COLORS,
            loop: true,
            seed: 94634
          })
        } catch (error) {
          if (vendorResizeListener) window.removeEventListener('resize', vendorResizeListener)
          throw error
        } finally {
          window.addEventListener = nativeAddEventListener
        }

        this.blurGradientInstance = instance
        this.blurGradientOriginalUpdate = typeof instance._update === 'function' ? instance._update : null
        this.blurGradientPaused = false
        this.blurGradientVendorResizeListener = vendorResizeListener

        const canvas = currentHost.querySelector('canvas')
        if (canvas) {
          canvas.setAttribute('aria-hidden', 'true')
          canvas.style.pointerEvents = 'none'
        }

        if (!this.dialogVisible) this.pauseBlurGradientBackground()
      } catch {
        // Keep the CSS fallback when WebGL or the optional background script is unavailable.
      } finally {
        this.blurGradientInitializing = false
      }
    },
    pauseBlurGradientBackground() {
      const instance = this.blurGradientInstance
      if (!instance || !this.blurGradientOriginalUpdate || this.blurGradientPaused) return
      instance._update = () => {}
      this.blurGradientPaused = true
    },
    resumeBlurGradientBackground() {
      const instance = this.blurGradientInstance
      if (!instance || !this.blurGradientOriginalUpdate || !this.blurGradientPaused) return
      instance._update = this.blurGradientOriginalUpdate
      this.blurGradientPaused = false
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(instance._update)
      }
    },
    destroyBlurGradientBackground() {
      const instance = this.blurGradientInstance
      if (!instance) return

      this.pauseBlurGradientBackground()
      if (this.blurGradientVendorResizeListener) {
        window.removeEventListener('resize', this.blurGradientVendorResizeListener)
        this.blurGradientVendorResizeListener = null
      }
      instance.resize = () => {}
      if (typeof instance.destroy === 'function') instance.destroy()
      this.blurGradientInstance = null
      this.blurGradientOriginalUpdate = null
      this.blurGradientPaused = false
    },
    open(options = {}) {
      return this.$store.dispatch('authDialog/open', options)
    },
    close() {
      if (this.submitting || this.googleLoading) return
      this.$store.dispatch('authDialog/close')
      this.$emit('close')
    },
    cancel() {
      this.$store.dispatch('authDialog/cancel')
      this.$emit('close')
    },
    handleDialogClosed() {
      this.resetTransientState()
      this.$emit('closed')
    },
    prepareForOpen() {
      this.activeTab = this.isTabEnabled(this.initialTab) ? this.initialTab : DEFAULT_LOGIN_TAB
      this.inlineError = ''
      this.agreed = false
      this.inviteInfo = null
      this.inviteInvalid = false
      this.inviteLookupCode = ''

      const routeInvite = this.$route && this.$route.query ? this.$route.query.invite_code : ''
      const inviteCode = this.storeInviteCode || routeInvite || this.readLegacyInviteCode()
      if (inviteCode && !this.storeInviteCode) {
        this.$store.dispatch('authDialog/setInviteCode', inviteCode)
      }
      if (inviteCode) this.lookupInvite(inviteCode)
    },
    readLegacyInviteCode() {
      if (typeof window === 'undefined') return ''
      return window.sessionStorage.getItem('invite_code') || ''
    },
    async lookupInvite(inviteCode) {
      const code = String(inviteCode || '').trim()
      if (!code || this.inviteLookupCode === code) return
      this.inviteLookupCode = code
      this.inviteLoading = true
      this.inviteInvalid = false
      try {
        const response = await getInviteInfoByCode(code)
        if (response.code === 0 && response.data && response.data.is_valid) {
          this.inviteInfo = {
            ...response.data,
            is_valid: true,
            inviter_user_name: response.data.inviter_user_name || '',
            reward_points: response.data.reward_points || 0
          }
        } else {
          this.inviteInfo = null
          this.inviteInvalid = true
        }
      } catch {
        this.inviteInfo = null
        this.inviteInvalid = true
      } finally {
        if (this.inviteLookupCode === code) this.inviteLoading = false
      }
    },
    formatNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toLocaleString() : '0'
    },
    isTabEnabled(tab) {
      if (tab === 'phone') return this.phoneLoginEnabled
      if (tab === 'password') return this.passwordLoginEnabled
      return tab === 'email'
    },
    switchTab(tab) {
      if (!this.isTabEnabled(tab)) return
      if (this.submitting || this.googleLoading || this.codeSending || this.activeTab === tab) return
      if (this.activeTab === 'password' && tab !== 'password') this.passwordForm.password = ''
      this.activeTab = tab
    },
    handleContactInput(value) {
      if (this.activeTab === 'phone') {
        this.phoneForm.phone = String(value || '').replace(/\D/g, '').slice(0, 11)
      }
      this.inlineError = ''
    },
    handleCodeInput(value) {
      this.codeValue = String(value || '').replace(/\D/g, '').slice(0, 6)
      this.inlineError = ''
    },
    handlePasswordInput() {
      this.inlineError = ''
    },
    validateContact() {
      const value = this.contactValue.trim()
      if (!value) {
        this.inlineError = this.$t(
          this.activeTab === 'phone'
            ? 'views.login.please_enter_your_mobile_number'
            : 'views.login.please_enter_your_email'
        )
        return false
      }
      if (this.activeTab === 'phone' && !PHONE_PATTERN.test(value)) {
        this.inlineError = this.$t('views.login.input_the_phone_number_correctly')
        return false
      }
      if (this.activeTab === 'email' && !EMAIL_PATTERN.test(value)) {
        this.inlineError = this.$t('views.login.email_check_error')
        return false
      }
      return true
    },
    validateCode() {
      if (!CODE_PATTERN.test(this.codeValue.trim())) {
        this.inlineError = this.$t('views.login.verification_code_invalid')
        return false
      }
      return true
    },
    validatePasswordLogin() {
      if (!this.contactValue.trim()) {
        this.inlineError = this.$t('views.login.please_enter_your_mobile_number_or_email')
        return false
      }
      if (!this.passwordForm.password.trim()) {
        this.inlineError = this.$t('views.login.please_enter_your_pwd')
        return false
      }
      return true
    },
    async sendCode() {
      if (this.codeSending || this.currentCountdown > 0 || this.activeTab === 'password') return
      this.inlineError = ''
      if (!this.validateContact()) return
      const tab = this.activeTab
      const contact = this.contactValue.trim()
      this.codeSending = true
      try {
        if (tab === 'phone') {
          await getSysSMSCode({ country_code: '86', phone: contact })
        } else {
          await getSysEmailCode({ email: contact, type: '1' })
        }
        this.codeValue = ''
        this.startCountdown(tab)
        ElMessage.success(this.$t('views.login.code_sent'))
      } catch (error) {
        this.inlineError = error && error.message
          ? error.message
          : this.$t('views.login.send_code_failed')
      } finally {
        this.codeSending = false
      }
    },
    startCountdown(tab) {
      this.clearTimer(tab)
      this.countdowns[tab] = 60
      this.timers[tab] = setInterval(() => {
        if (this.countdowns[tab] > 0) {
          this.countdowns[tab] -= 1
        } else {
          this.clearTimer(tab)
        }
      }, 1000)
    },
    clearTimer(tab) {
      if (this.timers[tab]) clearInterval(this.timers[tab])
      this.timers[tab] = null
    },
    clearAllTimers() {
      this.clearTimer('phone')
      this.clearTimer('email')
    },
    async submitLogin() {
      if (this.submitting || this.googleLoading) return
      this.inlineError = ''
      if (!this.agreed) {
        this.inlineError = this.$t('views.login.agreement_required')
        return
      }
      if (this.inviteLoading) return

      let params
      if (this.activeTab === 'password') {
        if (!this.validatePasswordLogin()) return
        params = {
          user_name: this.contactValue.trim(),
          password: this.passwordForm.password.trim(),
          login_type: 0
        }
      } else {
        if (!this.validateContact() || !this.validateCode()) return
        params = {
          user_name: this.contactValue.trim(),
          login_type: this.activeTab === 'phone' ? 1 : 3,
          valid_code: this.codeValue.trim()
        }
      }

      this.submitting = true
      try {
        if (this.effectiveInviteCode) params.invite_code = this.effectiveInviteCode

        const result = await this.$store.dispatch('user/login', params)
        if (!result) throw new Error(this.$t('views.login.login_failed'))

        const user = await this.$store.dispatch('user/getUserInfo')
        if (this.$websocket) {
          this.$websocket.close()
          this.$websocket.init(this)
        }

        const context = await this.$store.dispatch('authDialog/loginSuccess', { user })
        this.$emit('login-success', context)
        this.$emit('success', context)
        ElMessage.success(this.$t('views.login.login_success'))
        this.getUserTeam()
        this.restoreRedirect(context.redirect)
      } catch (error) {
        await this.$store.dispatch('user/resetAll')
        this.inlineError = error && error.message
          ? error.message
          : this.$t('views.login.login_failed')
      } finally {
        this.submitting = false
      }
    },
    async handleGoogleLogin() {
      if (this.googleLoading || this.submitting) return
      this.inlineError = ''
      this.googleLoading = true
      try {
        // OAuth 会离开当前页面，无显式目标时也保留任务所在的业务地址。
        if (!this.redirect) {
          await this.$store.dispatch('authDialog/setRedirect', this.$route.fullPath)
        }
        await this.$store.dispatch('authDialog/persist')
        const inviteCode = this.effectiveInviteCode
        if (inviteCode && typeof window !== 'undefined') {
          window.sessionStorage.setItem('invite_code', inviteCode)
        }
        const response = await getGoogleAuthUrl(inviteCode ? { invite_code: inviteCode } : {})
        if (!response.data || !response.data.auth_url) {
          throw new Error(this.$t('views.login.google_redirect_failed'))
        }
        localStorage.setItem('google_auth_url', response.data.auth_url)
        window.location.href = response.data.auth_url
      } catch (error) {
        this.inlineError = error && error.message
          ? error.message
          : this.$t('views.login.google_redirect_failed')
        this.googleLoading = false
      }
    },
    restoreRedirect(redirect) {
      if (!redirect || !this.$router) return
      this.$nextTick(() => {
        this.$router.push(redirect).catch(() => {})
      })
    },
    resetTransientState() {
      this.clearAllTimers()
      this.countdowns.phone = 0
      this.countdowns.email = 0
      this.phoneForm.code = ''
      this.emailForm.code = ''
      this.passwordForm.username = ''
      this.passwordForm.password = ''
      this.agreed = false
      this.submitting = false
      this.googleLoading = false
      this.codeSending = false
      this.inlineError = ''
    },
    getUserTeam() {
      return this.$store.dispatch('team/load').catch(() => [])
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/views/mobile/auth/styles/login_dialog' as mobile;

:global(.auth-login-dialog) {
  --login-ink: #161b2d;
  --login-muted: #778096;
  --login-line: #e7eaf0;
  --login-soft: #f5f7fb;
  --login-accent: var(--agst-color-primary, #2d66f0);
  --login-accent-deep: #1f4fc7;
  --login-radius: 22px;
}

:global(.auth-login-dialog.el-dialog) {
  overflow: hidden;
  max-width: calc(100vw - 32px);
  max-height: calc(100dvh - 32px);
  border-radius: var(--login-radius);
  background: #fff;
  box-shadow: 0 30px 90px rgba(17, 27, 55, 0.24);
}

:global(.auth-login-dialog.el-dialog .el-dialog__header) {
  display: none;
}

:global(.auth-login-dialog.el-dialog .el-dialog__body) {
  max-height: calc(100dvh - 32px);
  padding: 0;
}

.login-dialog-shell {
  position: relative;
  display: grid;
  height: min(500px, calc(100dvh - 32px));
  min-height: min(440px, calc(100dvh - 32px));
  grid-template-columns: minmax(250px, 0.9fr) minmax(390px, 1.1fr);
  overflow: hidden;
  max-height: calc(100dvh - 32px);
  background: #fff;
}

.login-dialog-shell--third-party {
  height: min(520px, calc(100dvh - 32px));

  .login-content {
    justify-content: flex-start;
    padding-top: 38px;
    padding-bottom: 28px;
  }

  .login-divider {
    margin: 22px 0 18px;
  }
}

.dialog-close {
  position: absolute;
  z-index: 5;
  top: 16px;
  right: 16px;
  display: grid;
  width: 34px;
  height: 34px;
  padding: 0;
  place-items: center;
  border: 0;
  border-radius: 50%;
  color: rgba(22, 27, 45, 0.62);
  background: rgba(255, 255, 255, 0.78);
  cursor: pointer;
  transition: color 160ms ease, background 160ms ease, transform 160ms ease;

  &:hover {
    color: var(--login-ink);
    background: #fff;
    transform: rotate(4deg);
  }

  &:focus-visible {
    outline: 3px solid rgba(45, 102, 240, 0.28);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
}

.login-visual {
  position: relative;
  display: grid;
  min-height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  grid-template-rows: auto minmax(0, 1fr) auto;
  align-items: center;
  justify-items: center;
  padding: 34px 38px 28px;
  color: #fff;
  isolation: isolate;
  border-radius: var(--login-radius);
  background:
    radial-gradient(ellipse at 42% 12%, rgba(255, 255, 255, 0.2), transparent 48%),
    radial-gradient(circle at 88% 82%, rgba(114, 155, 255, 0.32), transparent 54%),
    linear-gradient(160deg, #5a3d86 0%, #49649f 54%, #30477f 100%);
}

.blur-gradient-background {
  position: absolute;
  z-index: 0;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  border-radius: inherit;
  background:
    radial-gradient(ellipse at 42% 12%, rgba(255, 255, 255, 0.2), transparent 48%),
    radial-gradient(circle at 88% 82%, rgba(114, 155, 255, 0.32), transparent 54%),
    linear-gradient(160deg, #5a3d86 0%, #49649f 54%, #30477f 100%);

  :deep(canvas) {
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
}

.floating-bubbles {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.bubble {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.24), rgba(198, 213, 255, 0.08));
  opacity: 0;
  animation: bubbleFloat 10s ease-in-out infinite;

  &--1 {
    width: 60px;
    height: 60px;
    left: 15%;
    bottom: -30px;
    animation-delay: 0s;
  }

  &--2 {
    width: 45px;
    height: 45px;
    right: 20%;
    bottom: -25px;
    animation-delay: 3s;
  }

  &--3 {
    width: 50px;
    height: 50px;
    left: 55%;
    bottom: -25px;
    animation-delay: 6s;
  }
}

@keyframes bubbleFloat {
  0% {
    transform: translateY(0) scale(1);
    opacity: 0;
  }
  20% {
    opacity: 0.6;
  }
  80% {
    opacity: 0.3;
  }
  100% {
    transform: translateY(-600px) scale(1.2);
    opacity: 0;
  }
}

.visual-grid,
.visual-beam,
.visual-star,
.visual-eyebrow,
.visual-footnote {
  display: none;
}

.visual-brand-mark {
  position: relative;
  z-index: 2;
  width: 100px;
  height: auto;
  margin: 0 auto 12px;

  img {
    width: 100%;
    height: auto;
    display: block;
    filter: brightness(1.1);
    border-radius: 16px;
  }
}

.visual-copy {
  position: relative;
  z-index: 2;
  align-self: center;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  max-width: 320px;
}

.visual-hero-title-wrapper {
  display: flex;
  width: 100%;
  min-height: 82px;
  align-items: flex-start;
  justify-content: center;
}

.typewriter-wrapper {
  display: flex;
  width: 100%;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  margin-top: 0;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.fade-up-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-up-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.visual-hero-title {
  width: fit-content;
  max-width: 100%;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.25;
  color: #fff;
  margin: 0 auto 20px;
  letter-spacing: -0.02em;
  text-align: left;

  .visual-hero-prefix {
    display: block;
  }

  .visual-hero-highlight {
    display: block;
    background: linear-gradient(135deg, #a78bfa 0%, #c084fc 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.visual-hero-phrase {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.visual-features {
  position: relative;
  z-index: 2;
  width: max-content;
  max-width: 100%;
  justify-self: center;
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.visual-feature-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.96);
  margin-bottom: 14px;
  opacity: 0;
  transform: translateY(10px);
  animation: featureFadeIn 0.5s ease forwards;

  &--1 { animation-delay: 0.2s; }
  &--2 { animation-delay: 0.4s; }
  &--3 { animation-delay: 0.6s; }

  &:last-child {
    margin-bottom: 0;
  }
}

.visual-feature-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-right: 10px;
  color: var(--login-accent);
  font-weight: 700;
  flex-shrink: 0;
}

@keyframes featureFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-content {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 48px 54px 35px;
  color: var(--login-ink);
  background: #fff;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
}

.mobile-brand {
  display: none;
}

.mobile-login-registration {
  display: none;
}

.login-dialog-header {
  margin-bottom: 22px;
  text-align: center;

  .login-dialog-title {
    display: flex;
    align-items: baseline;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.35em;
    margin: 0;
    color: var(--login-ink);
    font-family: "Avenir Next", "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.04em;
    line-height: 1.18;
  }

  .login-dialog-brand {
    color: #7e308e;
    background: linear-gradient(100deg, #7e308e 0%, #7e308e 42%, #b36abe 50%, #7e308e 58%, #7e308e 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    margin: 8px 0 0;
    color: var(--login-muted);
    font-size: 13px;
    line-height: 1.55;
  }
}

.login-header {
  margin-bottom: 22px;

  .login-kicker {
    margin: 0 0 8px;
    color: var(--login-accent);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h1 {
    margin: 0;
    font-family: "Avenir Next", "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.04em;
    line-height: 1.18;
  }

  .login-subtitle {
    margin: 9px 0 0;
    color: var(--login-muted);
    font-size: 13px;
    line-height: 1.55;
  }
}

.login-reason {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 12px 0 0;
  color: #53698e;
  font-size: 12px;
  line-height: 1.5;
}

.invite-notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: -7px 0 18px;
  padding: 10px 12px;
  border: 1px solid #dce8ff;
  border-radius: 11px;
  color: #34558e;
  background: #f5f8ff;
  font-size: 12px;
  line-height: 1.45;

  .invite-notice-icon {
    flex: 0 0 auto;
    margin-top: 1px;
    color: var(--login-accent);
  }

  p {
    margin: 0;
  }

  .invite-title strong,
  .invite-reward strong {
    color: var(--login-accent-deep);
    font-weight: 700;
  }

  .invite-reward {
    margin-top: 2px;
    color: #6c7e9f;
  }

  .invite-invalid {
    color: #a66b2e;
  }
}

.login-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  margin-bottom: 21px;
  padding: 4px;
  border: 1px solid var(--login-line);
  border-radius: 14px;
  background: var(--login-soft);
}

.login-tabs--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.login-tab {
  position: relative;
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: 10px;
  color: var(--agst-text-color-regular, #606266);
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  transition: color 160ms ease, background 160ms ease, box-shadow 160ms ease;

  .el-icon {
    flex: 0 0 auto;
    font-size: 18px;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    color: var(--login-ink);
  }

  &.is-active {
    color: var(--login-accent);
    background: #fff;
    border-color: rgba(45, 102, 240, 0.12);
    box-shadow: 0 2px 8px rgba(45, 102, 240, 0.08);
  }

  &:focus-visible {
    outline: 3px solid rgba(45, 102, 240, 0.22);
    outline-offset: 1px;
  }
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.field-block {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 15px;

  label,
  .country-code {
    color: var(--agst-text-color-primary, #303133);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: none;
  }

  .country-code {
    color: var(--login-accent);
    letter-spacing: 0;
  }
}

.login-input {
  :deep(.el-input__wrapper) {
    min-height: 46px;
    padding: 1px 14px;
    border: 1px solid var(--login-line);
    border-radius: 11px;
    background: #fff;
    box-shadow: 0 0 0 0 transparent;
    transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
  }

  :deep(.el-input__wrapper:hover) {
    border-color: #cbd6ea;
  }

  :deep(.el-input__wrapper.is-focus) {
    border-color: var(--login-accent);
    background: #fbfdff;
    box-shadow: 0 0 0 3px rgba(45, 102, 240, 0.12);
  }

  :deep(.el-input__inner) {
    color: var(--login-ink);
    font-size: 14px;
  }

  :deep(.el-input__inner::placeholder) {
    color: var(--agst-text-color-secondary, #909399);
  }
}

.code-input-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 116px;
  gap: 8px;
}

.send-code-button {
  min-width: 0;
  padding: 0 8px;
  border: 1px solid #d5e1fb;
  border-radius: 11px;
  color: var(--login-accent-deep);
  background: #f5f8ff;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  transition: border-color 160ms ease, background 160ms ease, color 160ms ease;

  &:hover:not(:disabled) {
    border-color: var(--login-accent);
    color: #fff;
    background: var(--login-accent);
  }

  &:disabled {
    color: #aab6cc;
    background: #f7f8fb;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid rgba(45, 102, 240, 0.2);
    outline-offset: 2px;
  }
}

.inline-error {
  margin: -4px 0 -1px;
  color: #d04a4a;
  font-size: 12px;
  line-height: 1.45;
}

.agreement-check {
  align-items: flex-start;
  margin-top: 1px;
  white-space: normal;

  :deep(.el-checkbox__label) {
    padding-left: 7px;
    color: var(--agst-text-color-regular, #606266);
    font-size: 12px;
    line-height: 1.65;
  }

  :deep(.el-checkbox__inner) {
    border-color: #cbd3e1;
    border-radius: 4px;
  }

  :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    border-color: var(--login-accent);
    background: var(--login-accent);
  }

  a {
    color: var(--login-accent-deep);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .agreement-separator {
    color: #b6bdc9;
  }
}

.submit-login-button {
  width: 100%;
  min-height: 46px;
  margin-top: 1px;
  border: 0;
  border-radius: 11px;
  background: linear-gradient(112deg, var(--login-accent) 0%, #4c7cf4 100%);
  box-shadow: 0 9px 22px rgba(45, 102, 240, 0.2);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;

  &:hover:not(:disabled) {
    filter: saturate(1.08);
    box-shadow: 0 12px 26px rgba(45, 102, 240, 0.28);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
}

.login-divider {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  margin: 22px 0 14px;

  span {
    height: 1px;
    background: var(--login-line);
  }

  em {
    color: var(--agst-text-color-regular, #606266);
    font-size: 12px;
    font-weight: 500;
    font-style: normal;
  }
}

.google-login-button {
  display: flex;
  width: 100%;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px solid var(--login-line);
  border-radius: 11px;
  color: #3b4353;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 650;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;

  &:hover:not(:disabled) {
    border-color: #c9d4e9;
    background: #fbfcff;
    transform: translateY(-1px);
  }

  &:disabled {
    color: #aab1bd;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid rgba(45, 102, 240, 0.18);
    outline-offset: 2px;
  }
}

.google-icon {
  width: 18px;
  height: 18px;
}

.wechat-login-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 48px;
  margin-top: 12px;
  padding: 0 20px;
  border: 1.5px solid var(--login-line);
  border-radius: 10px;
  background: var(--login-soft);
  font-size: 15px;
  font-weight: 500;
  color: var(--login-muted);
  cursor: not-allowed;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
  }
}

.wechat-icon {
  width: 20px;
  height: 20px;
  margin-right: 10px;
  flex-shrink: 0;
  color: #09bb07;
}

:global(html.dark .auth-login-dialog) {
  --login-ink: #edf1f8;
  --login-muted: #9ba6b8;
  --login-line: #353c4a;
  --login-soft: #202632;
  --login-accent-deep: #8eafff;
}

:global(html.dark .auth-login-dialog.el-dialog) {
  border: 1px solid #343b48;
  background: #171b24;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.55);
}

:global(html.dark .auth-login-dialog.el-dialog .login-dialog-shell),
:global(html.dark .auth-login-dialog.el-dialog .login-content) {
  background: #171b24;
}

:global(html.dark .auth-login-dialog.el-dialog .dialog-close) {
  color: rgba(236, 241, 249, 0.72);
  background: rgba(38, 44, 57, 0.86);
}

:global(html.dark .auth-login-dialog.el-dialog .dialog-close:hover) {
  color: #fff;
  background: #313847;
}

:global(html.dark .auth-login-dialog.el-dialog .login-reason) {
  color: #a9bce0;
}

:global(html.dark .auth-login-dialog.el-dialog .invite-notice) {
  border-color: #304b74;
  color: #b6cdf3;
  background: #1c293d;
}

:global(html.dark .auth-login-dialog.el-dialog .invite-notice .invite-reward) {
  color: #92a7c7;
}

:global(html.dark .auth-login-dialog.el-dialog .invite-notice .invite-invalid) {
  color: #e4b57d;
}

:global(html.dark .auth-login-dialog.el-dialog .login-tab.is-active) {
  color: #f6f8fc;
  background: #303746;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
}

:global(html.dark .auth-login-dialog.el-dialog .field-label-row label) {
  color: #aab4c5;
}

:global(html.dark .auth-login-dialog.el-dialog .login-input .el-input__wrapper) {
  border-color: var(--login-line);
  background: #1d222c;
}

:global(html.dark .auth-login-dialog.el-dialog .login-input .el-input__wrapper:hover) {
  border-color: #4c5668;
}

:global(html.dark .auth-login-dialog.el-dialog .login-input .el-input__wrapper.is-focus) {
  border-color: var(--login-accent);
  background: #202735;
}

:global(html.dark .auth-login-dialog.el-dialog .login-input .el-input__inner) {
  color: var(--login-ink);
}

:global(html.dark .auth-login-dialog.el-dialog .send-code-button) {
  border-color: #38537e;
  color: #a9c2ff;
  background: #202b3f;
}

:global(html.dark .auth-login-dialog.el-dialog .send-code-button:disabled) {
  color: #6f7c91;
  background: #20242d;
}

:global(html.dark .auth-login-dialog.el-dialog .google-login-button) {
  border-color: var(--login-line);
  color: #e2e7f0;
  background: #1d222c;
}

:global(html.dark .auth-login-dialog.el-dialog .google-login-button:hover:not(:disabled)) {
  border-color: #4b5669;
  background: #252b37;
}

:global(html.dark .auth-login-dialog.el-dialog .agreement-check .el-checkbox__label) {
  color: #98a3b4;
}

@keyframes visual-float {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(0, -7px, 0); }
}

@keyframes visual-copy-in {
  from { opacity: 0; transform: translateY(9px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (min-width: 769px) and (max-height: 520px) {
  .login-visual {
    padding: 28px 28px 20px;
  }

  .visual-brand-mark {
    width: 74px;
    margin-bottom: 8px;
  }

  .visual-hero-title-wrapper {
    min-height: 76px;
  }

  .visual-hero-title {
    font-size: 28px;
    line-height: 1.2;
    margin-bottom: 12px;
  }

  .typewriter-wrapper {
    min-height: 42px;
  }

  .visual-hero-phrase {
    font-size: 13px;
    line-height: 1.45;
  }

  .visual-feature-item {
    margin-bottom: 8px;
    font-size: 12px;
  }
}

@include mobile.content;

@media (prefers-reduced-motion: reduce) {
  .visual-star,
  .visual-copy,
  .visual-footnote {
    animation: none;
  }
}
</style>
